import type { ResliceLapsResult } from '../../lib/circuitLapReslice';
import { selectedReslicedLapWindow } from '../../lib/circuitLapReslice';
import { buildReslicedLapReplayTimeline } from '../../lib/trackEnrichment';
import {
  getCachedLapMapPoints,
  lapMapPointsCacheKey,
  setCachedLapMapPoints,
} from '../../lib/sessionGeometryCache';
import {
  analyzeLap,
  buildLapReplayTimeline,
  buildMapPointsForGpsWindow,
  type MyChronSessionExport,
  type ReplayTimeline,
  type TrackMapPoint,
  trackSpeedRange,
} from '../../lib/telemetrySession';
import type { LapTimingMode } from './useSessionLapSlot';

const SESSION_COMPARE_GPS_MAX_POINTS = 12000;
const SESSION_COMPARE_SMOOTH_MAX_POINTS = 16000;
const SESSION_COMPARE_SMOOTH_MAX_SEGMENT_PX = 0.9;
/** Skip spline densification when the lap already has enough GPS samples for smooth replay. */
const SESSION_COMPARE_SMOOTH_MIN_RAW_POINTS = 900;

function splineValue(p0: number, p1: number, p2: number, p3: number, t: number) {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (
    (2 * p1)
    + (-p0 + p2) * t
    + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2
    + (-p0 + 3 * p1 - 3 * p2 + p3) * t3
  );
}

function smoothTrackMapPoints(points: TrackMapPoint[]): TrackMapPoint[] {
  if (points.length < 4 || points.length >= SESSION_COMPARE_SMOOTH_MIN_RAW_POINTS) {
    return points;
  }

  const smoothed: TrackMapPoint[] = [];

  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[Math.max(0, index - 1)]!;
    const p1 = points[index]!;
    const p2 = points[index + 1]!;
    const p3 = points[Math.min(points.length - 1, index + 2)]!;
    const segmentPx = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const steps = Math.min(18, Math.max(1, Math.ceil(segmentPx / SESSION_COMPARE_SMOOTH_MAX_SEGMENT_PX)));

    for (let step = 0; step < steps; step += 1) {
      if (smoothed.length >= SESSION_COMPARE_SMOOTH_MAX_POINTS - 1) {
        return [...smoothed, points.at(-1)!];
      }

      const t = step / steps;
      smoothed.push({
        x: splineValue(p0.x, p1.x, p2.x, p3.x, t),
        y: splineValue(p0.y, p1.y, p2.y, p3.y, t),
        lat: splineValue(p0.lat, p1.lat, p2.lat, p3.lat, t),
        lon: splineValue(p0.lon, p1.lon, p2.lon, p3.lon, t),
        speedKph: splineValue(p0.speedKph, p1.speedKph, p2.speedKph, p3.speedKph, t),
        timecodeMs: p1.timecodeMs + (p2.timecodeMs - p1.timecodeMs) * t,
      });
    }
  }

  smoothed.push(points.at(-1)!);
  return smoothed;
}

export function buildSessionLapMapPoints(
  session: MyChronSessionExport,
  lapNumber: number,
  lapTimingMode: LapTimingMode,
  circuitReslice?: ResliceLapsResult | null,
  sessionId: string | null = null,
): TrackMapPoint[] {
  const reslicedWindow = circuitReslice
    ? selectedReslicedLapWindow(circuitReslice, lapNumber)
    : null;
  const resliceKey = reslicedWindow
    ? `${reslicedWindow.startMs}:${reslicedWindow.endMs}`
    : 'logger';
  const cacheKey = lapMapPointsCacheKey({
    sessionId,
    lapNumber,
    lapTimingMode,
    resliceKey,
  });
  const cached = getCachedLapMapPoints(cacheKey);
  if (cached) {
    return cached;
  }

  let points: TrackMapPoint[] = [];

  if (lapTimingMode === 'circuit' && reslicedWindow && reslicedWindow.endMs > reslicedWindow.startMs) {
    points = smoothTrackMapPoints(buildMapPointsForGpsWindow(
      session,
      reslicedWindow.startMs,
      reslicedWindow.endMs,
      SESSION_COMPARE_GPS_MAX_POINTS,
    ));
  } else {
    const timeline = buildLapReplayTimeline(session, lapNumber);
    if (timeline) {
      points = smoothTrackMapPoints(buildMapPointsForGpsWindow(
        session,
        timeline.startTimecodeMs,
        timeline.endTimecodeMs,
        SESSION_COMPARE_GPS_MAX_POINTS,
      ));
    } else if (reslicedWindow && reslicedWindow.endMs > reslicedWindow.startMs) {
      points = smoothTrackMapPoints(buildMapPointsForGpsWindow(
        session,
        reslicedWindow.startMs,
        reslicedWindow.endMs,
        SESSION_COMPARE_GPS_MAX_POINTS,
      ));
    }
  }

  if (points.length >= 2) {
    setCachedLapMapPoints(cacheKey, points);
  }

  return points;
}

export function buildSessionLapAnalysis(
  session: MyChronSessionExport,
  lapNumber: number,
  lapTimingMode: LapTimingMode,
  circuitReslice?: ResliceLapsResult | null,
) {
  const reslicedWindow = circuitReslice
    ? selectedReslicedLapWindow(circuitReslice, lapNumber)
    : null;

  if (lapTimingMode === 'circuit' && reslicedWindow && reslicedWindow.endMs > reslicedWindow.startMs) {
    return analyzeLap(session, lapNumber, [], {
      windowOverride: reslicedWindow,
      rankPool: circuitReslice!.laps.map((lap) => ({
        lapNumber: lap.lapNumber,
        durationSec: lap.durationSec,
      })),
    });
  }

  const loggerAnalysis = analyzeLap(session, lapNumber, []);
  if (loggerAnalysis) {
    return loggerAnalysis;
  }

  if (reslicedWindow && reslicedWindow.endMs > reslicedWindow.startMs) {
    return analyzeLap(session, lapNumber, [], {
      windowOverride: reslicedWindow,
      rankPool: circuitReslice!.laps.map((lap) => ({
        lapNumber: lap.lapNumber,
        durationSec: lap.durationSec,
      })),
    });
  }

  return null;
}

export function buildSessionLapReplayTimeline(
  session: MyChronSessionExport,
  lapNumber: number,
  lapTimingMode: LapTimingMode,
  circuitReslice?: ResliceLapsResult | null,
): ReplayTimeline | null {
  if (lapTimingMode === 'circuit' && circuitReslice) {
    return buildReslicedLapReplayTimeline(circuitReslice, lapNumber);
  }

  const loggerTimeline = buildLapReplayTimeline(session, lapNumber);
  if (loggerTimeline) {
    return loggerTimeline;
  }

  return circuitReslice ? buildReslicedLapReplayTimeline(circuitReslice, lapNumber) : null;
}

export type SessionCompareReplayLayer = {
  id: string;
  sessionId: string | null;
  slotId?: string;
  lapNumber: number;
  lapTimingMode: LapTimingMode;
  label: string;
  color: string;
  points: TrackMapPoint[];
  analysis: NonNullable<ReturnType<typeof buildSessionLapAnalysis>>;
  sessionExport: MyChronSessionExport;
};

const PRIMARY_COMPARE_COLOR = '#B78CFF';
const COMPARE_CURSOR_COLORS = ['#FFB000', '#00D5FF', '#2EE66B', '#FF5C8A'];

export function buildSessionCompareReplayLayers(options: {
  primarySessionExport: MyChronSessionExport | null | undefined;
  primarySessionId: string | null;
  primaryLapNumber: number | null;
  primarySelectedCircuitLap: number | null;
  lapTimingMode: LapTimingMode;
  primaryCircuitReslice?: ResliceLapsResult | null;
  compareCircuitReslices?: Record<string, ResliceLapsResult | null | undefined>;
  compareSlots: Array<{ id?: string; sessionId: string; lapNumber: number | null }>;
  compareExports: Record<string, MyChronSessionExport | null | undefined>;
}): SessionCompareReplayLayer[] {
  const {
    primarySessionExport,
    primarySessionId,
    primaryLapNumber,
    primarySelectedCircuitLap,
    lapTimingMode,
    primaryCircuitReslice,
    compareCircuitReslices = {},
    compareSlots,
    compareExports,
  } = options;

  const layers: SessionCompareReplayLayer[] = [];
  const primaryLap =
    lapTimingMode === 'circuit' ? primarySelectedCircuitLap : primaryLapNumber;

  if (primarySessionExport && primaryLap != null) {
    const analysis = buildSessionLapAnalysis(
      primarySessionExport,
      primaryLap,
      lapTimingMode,
      primaryCircuitReslice,
    );
    const points = buildSessionLapMapPoints(
      primarySessionExport,
      primaryLap,
      lapTimingMode,
      primaryCircuitReslice,
      primarySessionId,
    );
    if (analysis) {
      layers.push({
        id: 'primary',
        sessionId: primarySessionId,
        lapNumber: primaryLap,
        lapTimingMode,
        label: `L${primaryLap}`,
        color: PRIMARY_COMPARE_COLOR,
        points,
        analysis,
        sessionExport: primarySessionExport,
      });
    }
  }

  for (const [compareIndex, slot] of compareSlots.entries()) {
    if (slot.lapNumber == null) {
      continue;
    }

    const sessionExport =
      slot.sessionId === primarySessionId
        ? primarySessionExport
        : compareExports[slot.sessionId];

    if (!sessionExport) {
      continue;
    }

    const circuitReslice =
      slot.sessionId === primarySessionId
        ? primaryCircuitReslice
        : compareCircuitReslices[slot.sessionId] ?? null;

    const analysis = buildSessionLapAnalysis(
      sessionExport,
      slot.lapNumber,
      lapTimingMode,
      circuitReslice,
    );
    const points = buildSessionLapMapPoints(
      sessionExport,
      slot.lapNumber,
      lapTimingMode,
      circuitReslice,
      slot.sessionId,
    );

    if (analysis) {
      layers.push({
        id: `compare-${compareIndex}`,
        slotId: slot.id,
        sessionId: slot.sessionId,
        lapNumber: slot.lapNumber,
        lapTimingMode,
        label: `L${slot.lapNumber}`,
        color: COMPARE_CURSOR_COLORS[compareIndex % COMPARE_CURSOR_COLORS.length]!,
        points,
        analysis,
        sessionExport,
      });
    }
  }

  return layers;
}

export function buildSessionCompareTrackGroups(options: {
  primarySessionExport: MyChronSessionExport | null | undefined;
  primarySessionId: string | null;
  primaryLapNumber: number | null;
  primarySelectedCircuitLap: number | null;
  lapTimingMode: LapTimingMode;
  primaryCircuitReslice?: ResliceLapsResult | null;
  compareCircuitReslices?: Record<string, ResliceLapsResult | null | undefined>;
  compareSlots: Array<{ id?: string; sessionId: string; lapNumber: number | null }>;
  compareExports: Record<string, MyChronSessionExport | null | undefined>;
}): TrackMapPoint[][] {
  const {
    primarySessionExport,
    primarySessionId,
    primaryLapNumber,
    primarySelectedCircuitLap,
    lapTimingMode,
    primaryCircuitReslice,
    compareCircuitReslices = {},
    compareSlots,
    compareExports,
  } = options;

  const groups: TrackMapPoint[][] = [];

  const primaryLap =
    lapTimingMode === 'circuit' ? primarySelectedCircuitLap : primaryLapNumber;

  if (primarySessionExport && primaryLap != null) {
    const primaryPoints = buildSessionLapMapPoints(
      primarySessionExport,
      primaryLap,
      lapTimingMode,
      primaryCircuitReslice,
      primarySessionId,
    );
    if (primaryPoints.length >= 2) {
      groups.push(primaryPoints);
    }
  }

  for (const slot of compareSlots) {
    if (slot.lapNumber == null) {
      continue;
    }

    const sessionExport =
      slot.sessionId === primarySessionId
        ? primarySessionExport
        : compareExports[slot.sessionId];

    if (!sessionExport) {
      continue;
    }

    const circuitReslice =
      slot.sessionId === primarySessionId
        ? primaryCircuitReslice
        : compareCircuitReslices[slot.sessionId] ?? null;

    const points = buildSessionLapMapPoints(
      sessionExport,
      slot.lapNumber,
      lapTimingMode,
      circuitReslice,
      slot.sessionId,
    );

    if (points.length >= 2) {
      groups.push(points);
    }
  }

  return groups;
}

export function buildCompareTrackSpeedRange(trackGroups: TrackMapPoint[][]) {
  const points = trackGroups.flat();
  return trackSpeedRange(points);
}
